<script setup lang="ts">
  // Linked orders of a caja movement (ported from -pos
  // ModalCajaMovementOrders.vue, online-only: ONE tenant-scoped batch fetch
  // via GET /orders/ids instead of the offline read model).
  // Opened via showDynamicDialog<boolean>(..., 'caja-movement-orders').
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import { OrderStatus } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { getOrderHumanTitle } from '@/domain/pos/order-formatting';
  import { roundPrice } from '@/utils';
  import OrdersService from '@/api/services/orders.service';
  import { useModal } from '@/composition/use-modal';
  import { useOrders } from '@/composition/use-orders';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const props = defineProps<{
    orderIds: number[];
  }>();

  const modals = useModal();
  const money = usePosMoney();
  const restaurantStore = useRestaurantStore();
  const ordersHelper = useOrders();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const isLoading = ref(true);
  const error = ref<string | null>(null);
  const orders = ref<IOrder[]>([]);

  async function load(): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      const res = await OrdersService.getByIds(
        restaurantStore.restaurant.id,
        props.orderIds
      );
      // Newest first, mirroring the -pos modal ordering.
      orders.value = [...res].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (e) {
      console.error('[caja-movement-orders] load failed', e);
      error.value = i18nT('cajaV2.movementOrdersLoadError');
    } finally {
      isLoading.value = false;
    }
  }

  onMounted(load);

  /** Requested ids the server dropped (foreign tenant / deleted). */
  const missingCount = computed(() => {
    if (isLoading.value || error.value) return 0;
    return Math.max(0, props.orderIds.length - orders.value.length);
  });

  function orderLabel(order: IOrder): string {
    return getOrderHumanTitle({
      type: order.type,
      humanId: order.humanId ?? null,
      tableName: order.table?.name ?? null,
      customerName: order.userName ?? null,
      customerPhone: order.userTel ?? null,
      deliveryAddress: order.userAddress ?? null,
      deliveryAddress2: order.userAddress2 ?? null,
    });
  }

  function formatDateTime(date: Date | string): string {
    return new Date(date).toLocaleString(proxy.$i18n.locale, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  }

  function pendingAmount(order: IOrder): number {
    return roundPrice(order.total - (order.totalPaid ?? 0));
  }

  function getStatusLabelKey(status: string): string | null {
    switch (status) {
      case OrderStatus.Progress:
        return 'posV2.statusProgress';
      case OrderStatus.ReadyToTake:
        return 'posV2.statusReady';
      case OrderStatus.Delivering:
        return 'posV2.statusDelivering';
      case OrderStatus.Finished:
        return 'posV2.statusFinished';
      case OrderStatus.Canceled:
        return 'posV2.statusCanceled';
      default:
        return null;
    }
  }

  /** Translated status label, or '' for unknown statuses (hides the badge). */
  function getStatusLabel(status: string): string {
    const key = getStatusLabelKey(status);
    return key ? i18nT(key) : '';
  }

  function getStatusColor(
    status: string
  ): 'primary' | 'success' | 'warning' | 'error' | 'neutral' | 'info' {
    switch (status) {
      case OrderStatus.Progress:
        return 'warning';
      case OrderStatus.ReadyToTake:
        return 'success';
      case OrderStatus.Delivering:
        return 'info';
      case OrderStatus.Finished:
        return 'neutral';
      case OrderStatus.Canceled:
        return 'error';
      default:
        return 'neutral';
    }
  }

  function openOrder(order: IOrder): void {
    // Close the list first, then open in-place (same as the single-order
    // path in useCajaMovementActions).
    close();
    void ordersHelper.openOrder(order.id, false);
  }

  function close(): void {
    modals.dialogSubmit('caja-movement-orders', true);
  }
</script>

<template>
  <div class="pos-v2-caja-movement-orders-modal">
    <div class="modal-header">
      <h2 class="modal-title">{{ $t('cajaV2.movementOrdersTitle') }}</h2>
      <span class="modal-subtitle">
        {{ $t('cajaV2.movementOrdersSubtitle') }}
      </span>
    </div>

    <div class="modal-body">
      <div v-if="isLoading" class="loading-state">
        <PosUiIcon name="loading" class="loading-icon" />
        <p>{{ $t('cajaV2.movementOrdersLoading') }}</p>
      </div>

      <div v-else-if="error" class="error-state">
        <PosUiIcon name="alert-circle-outline" class="error-icon" />
        <p class="error-text">{{ error }}</p>
        <PosUiButton
          color="primary"
          variant="soft"
          size="md"
          icon="refresh"
          @click="load"
        >
          {{ $t('posV2.retry') }}
        </PosUiButton>
      </div>

      <div v-else-if="!orders.length" class="empty-state">
        <PosUiIcon name="inbox-outline" class="empty-icon" />
        <p class="empty-text">{{ $t('cajaV2.movementOrdersEmpty') }}</p>
      </div>

      <div v-else class="orders-section">
        <div v-if="missingCount > 0" class="missing-alert">
          {{ $t('cajaV2.movementOrdersMissing', { count: missingCount }) }}
        </div>

        <div class="orders-list">
          <button
            v-for="order in orders"
            :key="order.id"
            type="button"
            class="order-card"
            @click="openOrder(order)"
          >
            <div class="card-left">
              <div class="order-label">{{ orderLabel(order) }}</div>
              <div class="order-details">
                <span class="detail-time">
                  <PosUiIcon name="clock-outline" class="detail-icon" />
                  {{ formatDateTime(order.createdAt) }}
                </span>
                <span class="detail-separator">·</span>
                <span class="detail-total">
                  {{ money.formatComputedMoney(order.total) }}
                </span>
                <template v-if="pendingAmount(order) > 0">
                  <span class="detail-separator">·</span>
                  <span class="detail-pending">
                    {{
                      $t('cajaV2.movementOrdersPending', {
                        amount: money.formatComputedMoney(
                          pendingAmount(order)
                        ),
                      })
                    }}
                  </span>
                </template>
              </div>
            </div>
            <div class="card-right">
              <PosUiBadge
                v-if="getStatusLabel(order.status)"
                :color="getStatusColor(order.status)"
                size="sm"
              >
                {{ getStatusLabel(order.status) }}
              </PosUiBadge>
              <PosUiIcon name="chevron-right" class="chevron-icon" />
            </div>
          </button>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="lg"
        class="footer-button"
        @click="close"
      >
        {{ $t('posV2.close') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-caja-movement-orders-modal {
    @apply tw-flex tw-w-full tw-flex-col tw-gap-4 tw-p-4;

    .modal-header {
      @apply tw-flex tw-flex-col tw-gap-0.5;

      .modal-title {
        @apply tw-text-lg tw-font-bold tw-text-neutral-900;
      }

      .modal-subtitle {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .modal-body {
      @apply tw-min-h-[200px];
    }

    .loading-state,
    .error-state,
    .empty-state {
      @apply tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-py-10 tw-text-neutral-500;

      .loading-icon,
      .error-icon,
      .empty-icon {
        @apply tw-text-4xl;
      }

      .loading-icon {
        @apply tw-animate-spin;
      }

      .error-icon {
        @apply tw-text-red-400;
      }

      .error-text,
      .empty-text {
        @apply tw-text-base;
      }
    }

    .orders-section {
      @apply tw-flex tw-flex-col tw-gap-3;

      .missing-alert {
        @apply tw-rounded-lg tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-sm tw-text-amber-800;
      }

      .orders-list {
        @apply tw-flex tw-max-h-[50vh] tw-flex-col tw-gap-2 tw-overflow-y-auto tw-p-0.5;
      }

      .order-card {
        @apply tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-transparent tw-bg-neutral-50 tw-px-4 tw-py-3 tw-transition-colors tw-duration-150;

        &:hover {
          @apply tw-border-neutral-200 tw-bg-neutral-100;

          .chevron-icon {
            @apply tw-translate-x-0.5 tw-text-neutral-600;
          }
        }

        .card-left {
          @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1 tw-text-left;

          .order-label {
            @apply tw-truncate tw-text-base tw-font-semibold tw-text-neutral-900;
          }

          .order-details {
            @apply tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-sm tw-text-neutral-500;

            .detail-icon {
              @apply tw-text-xs tw-opacity-70;
            }

            .detail-time {
              @apply tw-inline-flex tw-items-center tw-gap-1;
            }

            .detail-separator {
              @apply tw-text-neutral-300;
            }

            .detail-total {
              @apply tw-font-medium tw-tabular-nums tw-text-neutral-700;
            }

            .detail-pending {
              @apply tw-tabular-nums tw-text-amber-600;
            }
          }
        }

        .card-right {
          @apply tw-flex tw-flex-none tw-items-center tw-gap-2;

          .chevron-icon {
            @apply tw-text-lg tw-text-neutral-400 tw-transition-transform tw-duration-150;
          }
        }
      }
    }

    .modal-footer {
      @apply tw-flex tw-w-full tw-items-center tw-justify-end;

      :deep(.footer-button) {
        @apply tw-min-w-[130px];
      }
    }
  }
</style>
